import { useEffect, useMemo, useState } from 'react';
import i18next from 'i18next';
import { apiService } from '../services/api';
import { DataDictionary } from '../types';
import { getDictDisplayValue } from '../utils/i18n';

export interface DictionaryItem {
  code: string;
  labelZh: string;
  labelEn?: string;
}

interface UseDataDictionaryOptions {
  parentCode: string;
  autoLoad?: boolean;
}

interface UseDataDictionariesOptions {
  parentCodes: string[];
  autoLoad?: boolean;
}

const singleDictionaryCache: Record<string, DataDictionary[]> = {};
const batchDictionaryCache: Record<string, DataDictionary[]> = {};

export const clearDictionaryCache = () => {
  Object.keys(singleDictionaryCache).forEach(k => delete singleDictionaryCache[k]);
  Object.keys(batchDictionaryCache).forEach(k => delete batchDictionaryCache[k]);
};

const sortAndFilterDictionaries = (dictionaries?: DataDictionary[] | null): DataDictionary[] =>
  (dictionaries ?? [])
    .filter((dictionary) => dictionary.isActive)
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

const mapDictionaryItems = (dictionaries: DataDictionary[]): DictionaryItem[] =>
  dictionaries.map((dictionary) => ({
    code: dictionary.code,
    labelZh: dictionary.valueChinese,
    labelEn: dictionary.valueEnglish,
  }));

export function useDataDictionary(parentCode: string): DictionaryItem[];
export function useDataDictionary(options: UseDataDictionaryOptions): {
  dictionaries: DataDictionary[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  getDisplayValue: (dict: DataDictionary) => string;
  getValueByCode: (code: string | undefined) => string;
};
export function useDataDictionary(input: string | UseDataDictionaryOptions) {
  const options = typeof input === 'string' ? { parentCode: input, autoLoad: true } : input;
  const { parentCode, autoLoad = true } = options;
  const [dictionaries, setDictionaries] = useState<DataDictionary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dictionaryItems = useMemo(() => mapDictionaryItems(dictionaries), [dictionaries]);

  const loadDictionaries = async () => {
    if (!parentCode) {
      setDictionaries([]);
      return;
    }

    try {
      if (singleDictionaryCache[parentCode]) {
        setDictionaries(singleDictionaryCache[parentCode]);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);
      const response = await apiService.dataDictionaries.getByParentCode(parentCode);
      if (response?.success && response?.data) {
        const sorted = sortAndFilterDictionaries(response.data);
        singleDictionaryCache[parentCode] = sorted;
        batchDictionaryCache[parentCode] = sorted;
        setDictionaries(sorted);
      } else {
        setDictionaries([]);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : i18next.t('common.loadFailed', 'Failed to load');
      setError(message);
      setDictionaries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoLoad && parentCode) {
      loadDictionaries();
    }
  }, [parentCode, autoLoad]);

  // 根據 code 獲取顯示值
  const getValueByCode = (code: string | undefined): string => {
    if (!code) return '';
    const dict = dictionaries.find((d) => d.code === code || d.valueEnglish === code);
    return dict ? getDictDisplayValue(dict) : code;
  };

  if (typeof input === 'string') {
    return dictionaryItems;
  }

  return {
    dictionaries,
    loading,
    error,
    reload: loadDictionaries,
    getDisplayValue: getDictDisplayValue,
    getValueByCode,
  };
}

/**
 * 批量加載多個數據字典的 Hook
 * 使用批量 API 減少網路請求次數
 */
export const useDataDictionaries = ({ parentCodes, autoLoad = true }: UseDataDictionariesOptions) => {
  const initialDictionaries = useMemo(
    () =>
      parentCodes.flatMap((parentCode) =>
        batchDictionaryCache[parentCode] ?? singleDictionaryCache[parentCode] ?? [],
      ),
    [parentCodes],
  );

  const [allDictionaries, setAllDictionaries] = useState<DataDictionary[]>(initialDictionaries);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadDictionaries = async () => {
    if (!parentCodes || parentCodes.length === 0) {
      setAllDictionaries([]);
      return;
    }

    try {
      const cached = parentCodes.flatMap(
        (parentCode) => batchDictionaryCache[parentCode] ?? singleDictionaryCache[parentCode] ?? [],
      );
      const hasAllCached = parentCodes.every(
        (parentCode) => Boolean(batchDictionaryCache[parentCode] ?? singleDictionaryCache[parentCode]),
      );

      if (hasAllCached) {
        setAllDictionaries(cached);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);
      const response = await apiService.dataDictionaries.getByParentCodes(parentCodes);
      if (response?.success && response?.data) {
        const sorted = sortAndFilterDictionaries(response.data).sort((a, b) => {
          const parentCompare = (a.parentCode || '').localeCompare(b.parentCode || '');
          if (parentCompare !== 0) return parentCompare;
          return (a.displayOrder || 0) - (b.displayOrder || 0);
        });

        parentCodes.forEach((parentCode) => {
          const grouped = sorted.filter((dictionary) => dictionary.parentCode === parentCode);
          batchDictionaryCache[parentCode] = grouped;
          singleDictionaryCache[parentCode] = grouped;
        });

        setAllDictionaries(sorted);
      } else {
        setAllDictionaries([]);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : i18next.t('common.loadFailed', 'Failed to load');
      setError(message);
      setAllDictionaries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoLoad && parentCodes && parentCodes.length > 0) {
      loadDictionaries();
    }
  }, [parentCodes.join(','), autoLoad]); // 使用 join 來追蹤數組變化

  // 根據 parentCode 獲取對應的數據字典列表
  const getDictionariesByParentCode = (parentCode: string): DataDictionary[] => {
    return allDictionaries.filter(d => d.parentCode === parentCode);
  };

  // 根據 parentCode 和 code 獲取顯示值
  const getValueByCode = (parentCode: string, code: string | undefined): string => {
    if (!code) return '';
    const dict = allDictionaries.find(
      d => d.parentCode === parentCode && (d.code === code || d.valueEnglish === code)
    );
    return dict ? getDictDisplayValue(dict) : code;
  };

  // 為每個 parentCode 創建一個便捷的訪問器
  const dictionariesByParent = useMemo(() => {
    const map = new Map<string, DataDictionary[]>();
    parentCodes.forEach(parentCode => {
      map.set(parentCode, getDictionariesByParentCode(parentCode));
    });
    return map;
  }, [allDictionaries, parentCodes.join(',')]);

  return {
    allDictionaries,
    loading,
    error,
    reload: loadDictionaries,
    getDictionariesByParentCode,
    getDisplayValue: getDictDisplayValue,
    getValueByCode,
    dictionariesByParent, // 方便直接訪問，例如 dictionariesByParent.get('NATIONALITY')
  };
};

// 預定義的數據字典類型
export const DATA_DICTIONARY_TYPES = {
  LANGUAGE_SKILLS: 'LANGUAGE_SKILLS',
  OTHER_SKILLS: 'OTHER_SKILLS',
  NATIONALITY: 'NATIONALITY',
  RESPONSIBILITIES_CARE: 'RESPONSIBILITIES_CARE',
  HOUSEWORK: 'HOUSEWORK',
  COOKING_SKILLS: 'COOKING_SKILLS',
  RELIGION: 'RELIGION',
  BENEFITS: 'BENEFITS',
  JOB_LOCATION: 'JOB_LOCATION', // 工作地點（目前為國家級別，未來可擴展為地區）
  JOB_TITLE: 'JOB_TITLE', // 職位類型
  WORK_TYPE: 'WORK_TYPE', // 工作類型
  LIVING_ARRANGEMENT: 'LIVING_ARRANGEMENT', // 居住安排
  EDUCATION_LEVEL: 'EDUCATION_LEVEL', // 教育水平
  CONTRACT_SITUATION: 'CONTRACT_SITUATION', // 合約狀況
  MARITAL_STATUS: 'MARITAL_STATUS', // 婚姻狀況
  GENDER: 'GENDER', // 性別
  FAMILY_MEMBER_CATEGORY: 'FAMILY_MEMBER_CATEGORY',
  WORK_EXPERIENCE_SKILL: 'WORK_EXPERIENCE_SKILL',
  LANGUAGE_PROFICIENCY: 'LANGUAGE_PROFICIENCY',
} as const;

